import { CNVG, LAMS, L4B } from 'src/constants/ssoVariables';
import { getNavIcon } from '../NavMenuBar/navIconRegistry';
import { STATEMENT_OF_GOOD_HEALTH_GROUP_ID } from 'src/constants/navGroupIds';
// Mirrors the mobile nav gating in src/components/NavMenuBar/Mobile/MobileNav.tsx.
// The backend returns a payload pre-filtered by experience + isNewNav; these
// filters are additional mobile-only exclusions the client applies on top.
const DENTAL_NAV_IDS = ['DEN-PROD', 'DEN-NP', 'DENTAL-FORMS'];
const VISION_NAV_IDS = ['VIS', 'VIS_FORM'];
const DHMO_NAV_IDS = ['DHMO', 'DENTAL-HMO-FORMS'];
const isVisOrDhmoExternal = (id, externalLink) => (id === 'VIS' || id === 'DHMO') && !!externalLink;
// Filter for L2 links that live directly under an INavMenu (no groupLabel wrapper).
// Mirrors MobileNav.tsx:99-114.
const shouldRenderUngroupedLink = (navLink, isLfg) => {
    if (isVisOrDhmoExternal(navLink.id, navLink.externalLink))
        return false;
    if (isLfg && navLink.url.includes('lincoln4benefits'))
        return false;
    return true;
};
// Filter for L3 links inside a labelled INavGroup.
// Mirrors the union of MobileNav.tsx:126-144 (standard set) and MobileNav.tsx:147-160
// (LFG-only SSO/VIS/DHMO set).
const shouldRenderGroupedLink = (navLink, isLfg) => {
    if (isVisOrDhmoExternal(navLink.id, navLink.externalLink))
        return false;
    const isSsoOrProduct = !!navLink.cnvgSso ||
        !!navLink.lamsSSO ||
        VISION_NAV_IDS.includes(navLink.id ?? '') ||
        DHMO_NAV_IDS.includes(navLink.id ?? '');
    if (isSsoOrProduct)
        return isLfg;
    if (isLfg &&
        navLink.url.includes('lincoln4benefits') &&
        !DENTAL_NAV_IDS.includes(navLink.id ?? ''))
        return false;
    return true;
};
// Maps a single INavLink → the routing/tracking props (href, linkType, target,
// rel, onClick) honest-ui's NavLinkData / NavItemData expects. All routing
// concerns (basename prefixing, client-side vs full-page navigation, new-tab
// opening) are delegated to the library via linkType + target.
const buildLinkTargets = (navLink, user, mlpHomeUrl, trackPageEvent) => {
    const track = () => trackPageEvent(navLink.analyticsLabel ?? navLink.id ?? navLink.url, 'nav');
    // SSO — no destination href; onClick opens the SSO endpoint in a new window
    // (matches MobileNav.newOnClickFn).
    if (navLink.cnvgSso || navLink.lamsSSO || navLink.l4bSso) {
        const ssoGroup = navLink.cnvgSso ? CNVG : navLink.lamsSSO ? LAMS : L4B;
        return {
            onClick: () => {
                track();
                window.open(`${mlpHomeUrl}/customer/ssologin/${ssoGroup}`);
            },
        };
    }
    // Prove redirect: proveRequired items send the user to /prove until they've
    // verified their identity — applies whether the item would have been internal
    // or external otherwise (matches NavLink.tsx behavior).
    if (navLink.proveRequired && !user.isIdentityVerified) {
        return {
            href: '/prove',
            linkType: 'local',
            onClick: track,
        };
    }
    if (navLink.externalLink) {
        // honest-ui's Link auto-adds target='_blank' + rel='noopener noreferrer'
        // for linkType='external' — no need to spell them out.
        return {
            href: navLink.url,
            linkType: 'external',
            onClick: track,
        };
    }
    if (navLink.angular) {
        return {
            href: `${mlpHomeUrl}${navLink.url}`,
            linkType: 'external',
            onClick: track,
        };
    }
    return {
        href: navLink.url,
        linkType: 'local',
        onClick: track,
    };
};
const buildNavItems = ({ navigationMenu, user, translate, mlpHomeUrl, trackPageEvent, }) => {
    if (!navigationMenu)
        return [];
    const isLfg = !!user.isLfgUser;
    const items = [];
    for (const menu of navigationMenu) {
        // Case 1 — L1 is a direct link (no dropdown).
        if (menu.navLink) {
            if (!shouldRenderUngroupedLink(menu.navLink, isLfg))
                continue;
            items.push({
                label: translate(menu.navLabel),
                icon: getNavIcon(menu.navLink.iconName),
                ...buildLinkTargets(menu.navLink, user, mlpHomeUrl, trackPageEvent),
            });
            continue;
        }
        // Case 2 — L1 is a dropdown with one or more groups underneath.
        if (!menu.navGroups)
            continue;
        const groups = [];
        for (const group of menu.navGroups) {
            const filter = group.groupLabel ? shouldRenderGroupedLink : shouldRenderUngroupedLink;
            const links = [];
            for (const navLink of group.navLinks) {
                if (!filter(navLink, isLfg))
                    continue;
                links.push({
                    label: translate(navLink.label),
                    icon: getNavIcon(navLink.iconName),
                    ...buildLinkTargets(navLink, user, mlpHomeUrl, trackPageEvent),
                });
            }
            if (links.length > 0) {
                groups.push({
                    heading: group.id === STATEMENT_OF_GOOD_HEALTH_GROUP_ID
                        ? translate(menu.navLabel)
                        : group.groupLabel ? translate(group.groupLabel) : '',
                    links,
                });
            }
        }
        if (groups.length > 0) {
            items.push({
                label: translate(menu.navLabel),
                groups,
            });
        }
    }
    return items;
};
export default buildNavItems;
